import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Download, Edit3, Receipt, Shield, ShieldOff, UsersRound, Zap } from "lucide-react";
import { PageHeader, StatCard, StatusPill, EmptyState } from "../components/Primitives";
import { Modal } from "../components/Modal";
import { api, errorText, inr, formatDate } from "../lib/api";

export default function LibraryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [error, setError] = useState("");
  const [renewOpen, setRenewOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [form, setForm] = useState({ plan_id: "", start_date: "", note: "" });

  const load = async () => {
    try {
      const [libRes, planRes] = await Promise.all([api.get(`/libraries/${id}`), api.get("/plans")]);
      setData(libRes.data);
      setPlans(planRes.data.data);
      setForm((prev) => ({ ...prev, plan_id: libRes.data.data?.plan_id || "" }));
    } catch (err) {
      setError(errorText(err));
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  if (error) return <div className="error-message" data-testid="library-detail-error">{error}</div>;
  if (!data) return <div className="loading-screen"><span>Loading library…</span></div>;

  const library = data.data;
  const staff = data.staff || [];
  const invoices = data.invoices || [];
  const paid = invoices.filter((i) => i.status === "PAID").reduce((sum, i) => sum + Number(i.final_amount || 0), 0);
  const pending = invoices.filter((i) => i.status === "PENDING").reduce((sum, i) => sum + Number(i.final_amount || 0), 0);

  const toggleStatus = async () => {
    try {
      await api.patch(`/libraries/${id}/status`, { status: library.status === "ACTIVE" ? "BLOCKED" : "ACTIVE" });
      load();
    } catch (err) {
      setError(errorText(err));
    }
  };

  const renewSubmit = async (event) => {
    event.preventDefault();
    try {
      const payload = { plan_id: form.plan_id };
      if (form.start_date) payload.start_date = form.start_date;
      if (form.note) payload.note = form.note;
      await api.post(`/libraries/${id}/subscription`, payload);
      setRenewOpen(false);
      load();
    } catch (err) {
      setError(errorText(err));
    }
  };

  const markPaid = async (invoice) => {
    try {
      await api.post(`/libraries/${id}/invoices/${invoice.id}/mark-paid`);
      load();
    } catch (err) {
      setError(errorText(err));
    }
  };

  const openEdit = () => {
    setEditForm({
      name: library.name || "",
      owner_name: library.owner_name || "",
      email: library.email || "",
      mobile: library.mobile || "",
      address: library.address || "",
      city: library.city || "",
      state: library.state || "",
      pincode: library.pincode || "",
      total_seats: library.total_seats || 0,
      opening_time: library.opening_time || "06:00",
      closing_time: library.closing_time || "22:00",
      footer_text: library.footer_text || "",
      description: library.description || "",
      notes: library.notes || "",
    });
    setEditOpen(true);
    setError("");
  };

  const saveEdit = async (event) => {
    event.preventDefault();
    setEditSaving(true);
    setError("");
    try {
      const payload = { ...editForm, total_seats: Number(editForm.total_seats) };
      await api.patch(`/libraries/${id}`, payload);
      setEditOpen(false);
      load();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setEditSaving(false);
    }
  };

  const exportStudents = async () => {
    setExporting(true);
    try {
      const response = await api.get(`/libraries/${id}/export/students`, { responseType: "blob" });
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const safeName = (library?.name || "library").replace(/[^A-Za-z0-9]+/g, "_").slice(0, 60);
      link.download = `${safeName}_students.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="page-enter">
      <Link to="/libraries" className="back-link" data-testid="back-to-libraries"><ArrowLeft size={16} /> Back to libraries</Link>
      <PageHeader
        eyebrow="LIBRARY DRILL-DOWN"
        title={library.name}
        description={`${library.city || ""} · ${library.owner_name} · plan ${library.plan}`}
        action={
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button className="button button-outline" onClick={openEdit} data-testid="edit-library-button">
              <Edit3 size={16} /> Edit profile
            </button>
            <button className="button button-outline" onClick={exportStudents} disabled={exporting} data-testid="export-library-students-button">
              <Download size={16} /> {exporting ? "Exporting…" : `Export students (${library.students ?? 0})`}
            </button>
            <button className="button button-outline" onClick={toggleStatus} data-testid="toggle-status-button">
              {library.status === "ACTIVE" ? <><ShieldOff size={16} /> Block</> : <><Shield size={16} /> Unblock</>}
            </button>
            <button className="button button-primary" onClick={() => setRenewOpen(true)} data-testid="renew-plan-button">
              <Zap size={16} /> Renew / change plan
            </button>
          </div>
        }
      />

      <div className="stat-grid">
        <StatCard label="Students" value={library.students ?? 0} detail="Active roster" icon={<UsersRound size={18} />} />
        <StatCard label="Occupied seats" value={`${library.occupied_seats ?? 0}/${library.seats ?? library.total_seats ?? 0}`} detail="Live floor" tone="green" icon={<UsersRound size={18} />} />
        <StatCard label="Paid revenue" value={inr(paid)} detail={`${invoices.filter((i) => i.status === "PAID").length} invoices`} tone="dark" icon={<Receipt size={18} />} />
        <StatCard label="Pending revenue" value={inr(pending)} detail={`${invoices.filter((i) => i.status === "PENDING").length} pending`} tone="orange" icon={<Receipt size={18} />} />
      </div>

      <section className="content-card" style={{ marginBottom: 16 }}>
        <div className="card-heading"><div><span className="card-kicker">PROFILE</span><h2>Library snapshot</h2></div><StatusPill status={library.status} /></div>
        <div className="profile-grid" style={{ marginTop: 20 }}>
          <div><span>Owner email</span><strong>{library.email}</strong></div>
          <div><span>Mobile</span><strong>{library.mobile}</strong></div>
          <div><span>City</span><strong>{library.city || "—"}, {library.state || ""}</strong></div>
          <div><span>Opening hours</span><strong>{library.opening_time} — {library.closing_time}</strong></div>
          <div><span>Plan</span><strong>{library.plan}</strong></div>
          <div><span>Subscription ends</span><strong>{formatDate(library.subscription_end)}</strong></div>
        </div>
      </section>

      <div className="dashboard-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
        <section className="content-card">
          <div className="card-heading"><div><span className="card-kicker">STAFF</span><h2>{staff.length} accounts</h2></div></div>
          {staff.length ? (
            <ul className="notification-list" style={{ marginTop: 18 }} data-testid="library-staff-list">
              {staff.map((member) => (
                <li key={member.id} className="notification-item">
                  <div className="notification-icon"><UsersRound size={18} /></div>
                  <div>
                    <strong>{member.name}</strong>
                    <p>{member.email}</p>
                    <small>{member.role} · joined {formatDate(member.created_at)}</small>
                  </div>
                  <StatusPill status={member.status} />
                </li>
              ))}
            </ul>
          ) : <EmptyState icon={<UsersRound size={22} />} title="No staff yet" hint="The library admin can add their team from the Staff screen." />}
        </section>

        <section className="content-card">
          <div className="card-heading"><div><span className="card-kicker">INVOICES</span><h2>{invoices.length} invoices</h2></div></div>
          {invoices.length ? (
            <div className="table-wrap">
              <table data-testid="library-invoices-table">
                <thead><tr><th>Invoice</th><th>Plan</th><th>Amount</th><th>Status</th><th>Action</th></tr></thead>
                <tbody>
                  {invoices.map((invoice) => (
                    <tr key={invoice.id}>
                      <td><strong>{invoice.invoice_number}</strong><br /><small style={{ color: "#64748b" }}>{formatDate(invoice.billing_date)}</small></td>
                      <td>{invoice.plan_name}</td>
                      <td>{inr(invoice.final_amount)}</td>
                      <td><StatusPill status={invoice.status} /></td>
                      <td>{invoice.status !== "PAID" ? <button className="button button-outline button-small" onClick={() => markPaid(invoice)} data-testid={`mark-invoice-paid-${invoice.id}`}>Mark paid</button> : <span style={{ color: "#16a34a", fontSize: 11 }}>✓ Paid</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <EmptyState icon={<Receipt size={22} />} title="No invoices" hint="Create or renew a subscription to generate the first invoice." />}
        </section>
      </div>

      {editOpen ? (
        <Modal title={`Edit ${library.name}`} eyebrow="LIBRARY PROFILE" close={() => setEditOpen(false)} testId="edit-library-modal" wide>
          <form className="student-form" onSubmit={saveEdit}>
            {error ? <div className="error-message" data-testid="edit-library-error">{error}</div> : null}
            <div className="form-section">
              <div className="form-section-head"><span className="hero-label">LIBRARY</span><h3>Basic details</h3></div>
              <div className="form-grid">
                <label>Library name<input required value={editForm.name} onChange={(event) => setEditForm({ ...editForm, name: event.target.value })} data-testid="edit-library-name" /></label>
                <label>City<input required value={editForm.city} onChange={(event) => setEditForm({ ...editForm, city: event.target.value })} data-testid="edit-library-city" /></label>
                <label>State<input value={editForm.state} onChange={(event) => setEditForm({ ...editForm, state: event.target.value })} /></label>
                <label>Pincode<input value={editForm.pincode} onChange={(event) => setEditForm({ ...editForm, pincode: event.target.value })} /></label>
                <label className="field-full">Address<input value={editForm.address} onChange={(event) => setEditForm({ ...editForm, address: event.target.value })} /></label>
                <label>Total seats<input type="number" min="1" value={editForm.total_seats} onChange={(event) => setEditForm({ ...editForm, total_seats: event.target.value })} data-testid="edit-library-seats" /></label>
                <label>Opening time<input type="time" value={editForm.opening_time} onChange={(event) => setEditForm({ ...editForm, opening_time: event.target.value })} /></label>
                <label>Closing time<input type="time" value={editForm.closing_time} onChange={(event) => setEditForm({ ...editForm, closing_time: event.target.value })} /></label>
              </div>
            </div>
            <div className="form-section">
              <div className="form-section-head"><span className="hero-label">OWNER</span><h3>Primary admin contact</h3></div>
              <div className="form-grid">
                <label>Owner name<input required value={editForm.owner_name} onChange={(event) => setEditForm({ ...editForm, owner_name: event.target.value })} data-testid="edit-library-owner" /></label>
                <label>Owner email<input type="email" required value={editForm.email} onChange={(event) => setEditForm({ ...editForm, email: event.target.value })} data-testid="edit-library-email" /></label>
                <label>Mobile<input required value={editForm.mobile} onChange={(event) => setEditForm({ ...editForm, mobile: event.target.value })} data-testid="edit-library-mobile" /></label>
              </div>
            </div>
            <div className="form-section">
              <div className="form-section-head"><span className="hero-label">BRANDING</span><h3>Receipts & footer</h3></div>
              <div className="form-grid">
                <label className="field-full">Footer / receipt text<input value={editForm.footer_text} onChange={(event) => setEditForm({ ...editForm, footer_text: event.target.value })} data-testid="edit-library-footer" placeholder="Powered by MKS Digital Technology" /></label>
                <label className="field-full">Description<input value={editForm.description} onChange={(event) => setEditForm({ ...editForm, description: event.target.value })} /></label>
                <label className="field-full">Internal notes<input value={editForm.notes} onChange={(event) => setEditForm({ ...editForm, notes: event.target.value })} /></label>
              </div>
            </div>
            <div className="modal-actions sticky-actions">
              <button type="button" className="button button-outline" onClick={() => setEditOpen(false)}>Cancel</button>
              <button type="submit" className="button button-primary" disabled={editSaving} data-testid="save-library-edit">
                {editSaving ? "Saving…" : "Save changes"} <ArrowRight size={16} />
              </button>
            </div>
          </form>
        </Modal>
      ) : null}

      {renewOpen ? (
        <Modal title="Renew / change plan" eyebrow="SUBSCRIPTION" close={() => setRenewOpen(false)} testId="renew-plan-modal">
          <form className="modal-form" onSubmit={renewSubmit}>
            <label>Plan
              <select required value={form.plan_id} onChange={(event) => setForm({ ...form, plan_id: event.target.value })} data-testid="renew-plan-select">
                <option value="">Select plan</option>
                {plans.map((plan) => <option key={plan.id} value={plan.id}>{plan.name} · {inr(plan.price)}</option>)}
              </select>
            </label>
            <label>Start date (optional, defaults to today)
              <input type="date" value={form.start_date} onChange={(event) => setForm({ ...form, start_date: event.target.value })} data-testid="renew-plan-start" />
            </label>
            <label>Note<input value={form.note} onChange={(event) => setForm({ ...form, note: event.target.value })} /></label>
            <div className="modal-actions">
              <button type="button" className="button button-outline" onClick={() => setRenewOpen(false)}>Cancel</button>
              <button className="button button-primary" type="submit" data-testid="save-renew-plan"><ArrowRight size={16} /> Renew subscription</button>
            </div>
          </form>
        </Modal>
      ) : null}
    </div>
  );
}
